import type { ComponentContentSchema, PageDataMap } from '@vibesites/runtime';
import { isActivePage, mergePages } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
// Same parent+local merge the Header uses for nav, so HTML sitemap links
// respect this theme's path overrides (e.g. /contact-us vs parent /contact).
import parentPagesData from '../parent-pages.json';
import localPagesData from '../pages.json';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface Review {
  rating?: number | string | null;
  review_text?: string | null;
}

/**
 * Upstream feeds sometimes pass literal strings like "false", "null", "none"
 * instead of an empty value — treat those as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'undefined' || lower === 'none' || lower === 'n/a') {
    return false;
  }
  return true;
}

// Only live, visible pages — drop soft-deleted entries (e.g. Floor Plans
// Details), the sitemap page itself, and template pages (e.g. hyperlocal).
const allPages = mergePages(
  parentPagesData as PageDataMap,
  localPagesData as PageDataMap,
).filter((p) => isActivePage(p) && p.visible !== false && p.path !== '/sitemap' && !p.template);

export function SitemapContent() {
  // Hide /virtual-tour unless the property has at least one tour with a
  // usable URL (matches VirtualTourEmbed's display rule), and hide /reviews
  // unless the property actually has reviews.
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const reviews = useCollectionBinding<Review>('sitemapcontent.reviews', {
    collection: 'reviews',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));
  const hasReviews = (reviews || []).length > 0;

  const pages = allPages.filter((p) => {
    if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
    if (p.path === '/reviews' && !hasReviews) return false;
    return true;
  });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:bg-black hover:text-white transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
    'sitemapcontent.reviews': {
      label: 'Reviews (visibility check)',
      collection: 'reviews',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
